let vapidPublicKey = process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY || '';

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
};

const isAndroid = () => {
  if (typeof navigator === 'undefined') return false;
  return /android/i.test(navigator.userAgent);
};

const isChrome = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return ua.includes('Chrome') && !ua.includes('Edg');
};

const needsStandaloneIOS = () => {
  if (typeof window === 'undefined') return false;
  return isIOS() && !window.matchMedia('(display-mode: standalone)').matches;
};

export const setVapidPublicKey = (key) => {
  vapidPublicKey = key || '';
};

export const getPushSupportStatus = () => {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return { supported: false, reason: 'unknown' };
    }

    if (!('Notification' in window)) {
      return { supported: false, reason: 'notification' };
    }

    if (needsStandaloneIOS()) {
      return { supported: false, reason: 'iosStandalone' };
    }

    if (!('serviceWorker' in navigator)) {
      return { supported: false, reason: 'serviceWorker' };
    }

    if (!vapidPublicKey) {
      return { supported: false, reason: 'missingKey' };
    }

    return { supported: true };
  } catch (error) {
    return { supported: false, reason: 'unknown' };
  }
};

export const isPushSupported = () => getPushSupportStatus().supported;

/** SW kaydı + aktif olma beklemesi (iOS/Safari race fix) */
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker desteklenmiyor');
  }

  try {
    await navigator.serviceWorker.register('/notification-sw.js', {
      scope: '/'
    });
    const registration = await navigator.serviceWorker.ready;

    if (!registration.active) {
      const pending = registration.installing || registration.waiting;
      if (pending) {
        await new Promise((resolve) => {
          const onStateChange = () => {
            if (pending.state === 'activated' || pending.state === 'redundant') {
              pending.removeEventListener('statechange', onStateChange);
              resolve();
            }
          };
          pending.addEventListener('statechange', onStateChange);
          // Güvenlik zaman aşımı
          setTimeout(() => {
            pending.removeEventListener('statechange', onStateChange);
            resolve();
          }, 8000);
        });
      }
    }

    return navigator.serviceWorker.ready;
  } catch (error) {
    throw new Error(`Service Worker kaydı başarısız: ${error.message}`);
  }
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const arrayBufferToBase64 = (buffer) => {
  if (!buffer) return null;
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

export const subscribeToPush = async (registration) => {
  if (!vapidPublicKey) {
    throw new Error('VAPID public key tanımlı değil');
  }

  if (!('PushManager' in window)) {
    throw new Error('Push Manager desteklenmiyor');
  }

  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
    return subscription;
  } catch (error) {
    if (error.name === 'NotAllowedError') {
      throw new Error('Bildirim izni verilmedi');
    }
    throw new Error(`Push aboneliği başarısız: ${error.message}`);
  }
};

/** Mevcut aboneliği al; yoksa yeni oluştur */
export const getOrCreatePushSubscription = async (registration) => {
  const activeRegistration = registration?.active
    ? registration
    : await navigator.serviceWorker.ready;

  if (!activeRegistration?.pushManager) {
    throw new Error('Push Manager bu tarayıcıda desteklenmiyor');
  }

  if (!activeRegistration.active && !navigator.serviceWorker.controller) {
    throw new Error('Service worker henüz aktif değil; sayfayı yenileyip tekrar deneyin');
  }

  let subscription = await activeRegistration.pushManager.getSubscription();
  if (subscription) {
    return subscription;
  }

  return subscribeToPush(activeRegistration);
};

export const serializePushSubscription = (subscription) => {
  if (!subscription?.endpoint) {
    throw new Error('Subscription endpoint bulunamadı.');
  }

  const p256dhKey = subscription.getKey('p256dh');
  const authKey = subscription.getKey('auth');
  if (!p256dhKey || !authKey) {
    throw new Error('Subscription keys bulunamadı.');
  }

  const p256dhBase64 = arrayBufferToBase64(p256dhKey);
  const authBase64 = arrayBufferToBase64(authKey);
  if (!p256dhBase64 || !authBase64) {
    throw new Error('Subscription keys serialize edilemedi.');
  }

  const userAgent = navigator.userAgent || '';
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const browser = userAgent.includes('Edg')
    ? 'edge'
    : userAgent.includes('Chrome')
      ? 'chrome'
      : userAgent.includes('Firefox')
        ? 'firefox'
        : userAgent.includes('Safari')
          ? 'safari'
          : 'unknown';

  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: p256dhBase64,
      auth: authBase64
    },
    metadata: {
      userAgent,
      browser,
      deviceType: isMobile ? 'mobile' : 'desktop',
      timestamp: new Date().toISOString()
    }
  };
};

export const hasPushSubscriptions = (preferences) => {
  const subs = preferences?.pushSubscription;
  if (Array.isArray(subs)) return subs.length > 0;
  return Boolean(subs?.endpoint);
};

export { isIOS, isAndroid, isChrome };
